// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.

import { pdfjs, ensurePdfWorker } from '../../lib/pdfWorker'
import { useEffect, useRef, useState, useCallback } from 'react'
import { Pen, Calendar, Type, CheckSquare, User, Briefcase, Save, Check } from 'lucide-react'
import * as api from '../../api/client'
import {
  pixelToPoint, pointToPixel, validatePlacement, DEFAULT_FIELD_SIZE,
  FIELD_TYPES, FIELD_LABEL, type FieldType, type PagePoints,
} from '../../lib/esignCoords'

// pdf.js worker — served from the same origin as the bundle.

const FIELD_ICON: Record<FieldType, React.ComponentType<any>> = {
  signature: Pen, date_signed: Calendar, initials: Type, text: Type, checkbox: CheckSquare, name: User, title: Briefcase,
}
const FIELD_COLOR: Record<FieldType, string> = {
  signature: '#0891b2', date_signed: '#7c3aed', initials: '#0d9488', text: '#2563eb', checkbox: '#ca8a04', name: '#db2777', title: '#475569',
}

// Signer roles the system understands (from the workflow code: patient, prescriber/provider,
// pharmacist, nurse) plus caregiver/witness common on consent forms. Each gets a color so a
// multi-signer template is visually clear — a field's marker is colored by WHO signs it.
const RECIPIENT_ROLES = ['patient', 'prescriber', 'nurse', 'pharmacist', 'caregiver', 'witness'] as const
type RecipientRole = typeof RECIPIENT_ROLES[number]
const RECIPIENT_LABEL: Record<RecipientRole, string> = {
  patient: 'Patient', prescriber: 'Prescriber', nurse: 'Nurse', pharmacist: 'Pharmacist', caregiver: 'Caregiver', witness: 'Witness',
}
const RECIPIENT_COLOR: Record<RecipientRole, string> = {
  patient: '#0891b2', prescriber: '#7c3aed', nurse: '#059669', pharmacist: '#ca8a04', caregiver: '#db2777', witness: '#475569',
}

// Auto-fill vocabulary for TEXT fields — MUST match backend EsignDataBinding.Keys/Labels exactly.
const DATA_BINDINGS: { key: string; label: string }[] = [
  { key: '', label: 'Signer fills it (no auto-fill)' },
  { key: 'patient_name', label: 'Patient name' },
  { key: 'patient_dob', label: 'Patient date of birth' },
  { key: 'drug_name', label: 'Drug / therapy' },
  { key: 'diagnosis', label: 'Diagnosis' },
  { key: 'visit_date', label: 'Visit date' },
  { key: 'prescriber_name', label: 'Prescriber name' },
  { key: 'prescriber_npi', label: 'Prescriber NPI' },
  { key: 'pharmacy_name', label: 'Pharmacy name' },
  { key: 'today_date', label: "Today's date" },
]

interface PlacedField extends api.EsignPlacement { _key: string }

/**
 * Visual field-placement editor. Renders a template PDF page-by-page; the admin selects a field
 * type then clicks on the page to drop it. Coordinates are captured in PDF points (render-independent)
 * via pixelToPoint, so zoom never affects where the field lands on the signed document.
 */
export default function EsignFieldEditor({ templateId }: { templateId: string }) {
  // Responsive: on narrow screens stack the palette above a full-width PDF. The editor was a fixed
  // horizontal flex (180px palette + remaining width for the PDF), which on a phone squeezed the
  // PDF canvases to ~178px — pages rendered tiny/invisible. This switches to vertical stacking.
  const [isNarrow, setIsNarrow] = useState<boolean>(
    typeof window !== 'undefined' ? window.innerWidth < 720 : false)
  useEffect(() => {
    const onResize = () => setIsNarrow(window.innerWidth < 720)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])
  const [pages, setPages] = useState<PagePoints[]>([])
  const [renderWidths, setRenderWidths] = useState<Record<number, number>>({})
  const [fields, setFields] = useState<PlacedField[]>([])
  const [tool, setTool] = useState<FieldType>('signature')
  const [recipient, setRecipient] = useState<RecipientRole>('patient')
  // For TEXT fields: optionally auto-fill from a patient/assessment value (data-binding). Empty = the
  // signer fills it. Vocabulary mirrors the backend EsignDataBinding.Keys exactly (human-confirmed map).
  const [dataBinding, setDataBinding] = useState<string>('')
  const [previewing, setPreviewing] = useState(false)
  const [preview, setPreview] = useState<{ data_source: string; fields: api.EsignPreviewField[] } | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  // On-screen diagnostics — so a failure on a device we can't inspect shows what happened, on the
  // page itself, instead of a silent blank. Temporary troubleshooting aid.
  const [diag, setDiag] = useState<string[]>([])
  const addDiag = useCallback((s: string) => setDiag(d => [...d, s]), [])
  const canvasRefs = useRef<Record<number, HTMLCanvasElement | null>>({})
  const pdfDocRef = useRef<any>(null)
  // Zoom (1 = fit width). Buttons are the reliable control on all devices; pinch is wired too.
  const [zoom, setZoom] = useState(1)
  // Active drag of a placed field: which field, and the pointer offset within it.
  const dragRef = useRef<{ key: string; page: PagePoints; offX: number; offY: number } | null>(null)
  // Pinch tracking: distance between two active pointers.
  const pinchRef = useRef<{ startDist: number; startZoom: number } | null>(null)
  const pointersRef = useRef<Map<number, { x: number; y: number }>>(new Map())

  // Load the PDF + existing placements.
  useEffect(() => {
    let cancelled = false
    async function load() {
      setLoading(true); setErr(null); setDiag([])
      try {
        addDiag(`device: ${window.innerWidth}×${window.innerHeight}, dpr ${window.devicePixelRatio}`)
        const existing = await api.getEsignTemplate(templateId)
        if (!cancelled) {
          setFields(existing.placements.map((p, i) => ({ ...p, _key: `e${i}` })))
        }
        ensurePdfWorker()
        addDiag(`worker: ${pdfjs.GlobalWorkerOptions.workerSrc ? 'set' : 'MISSING'}`)
        // Token-first: the PDF endpoint needs the per-tab Bearer (a cookie-only fetch 401/403s).
        const pdfResp = await api.apiFetch(`/carerx/esign/templates/${templateId}/pdf`)
        addDiag(`pdf fetch: HTTP ${pdfResp.status}`)
        if (!pdfResp.ok) throw new Error(`Could not load PDF (HTTP ${pdfResp.status})`)
        const pdfBytes = await pdfResp.arrayBuffer()
        addDiag(`pdf bytes: ${pdfBytes.byteLength}`)
        if (pdfBytes.byteLength === 0) throw new Error('PDF came back empty')
        const task = pdfjs.getDocument({ data: pdfBytes })
        const pdf = await task.promise
        addDiag(`pdf parsed: ${pdf.numPages} page(s)`)
        const pageDims: PagePoints[] = []
        for (let n = 1; n <= pdf.numPages; n++) {
          const page = await pdf.getPage(n)
          const vp = page.getViewport({ scale: 1 })
          pageDims.push({ page: n, w: vp.width, h: vp.height })
        }
        if (cancelled) return
        pdfDocRef.current = pdf
        setPages(pageDims)
        // NB: do NOT render here. Canvases mount from `pages` on the next React commit; rendering
        // immediately (even via setTimeout(0)) races that mount and finds no canvas element — the
        // mobile blank bug. An effect keyed on `pages` renders once the canvases actually exist.
      } catch (e: any) {
        addDiag(`ERROR: ${e?.message || e}`)
        if (!cancelled) setErr(e?.message || 'Failed to load template PDF')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void load()
    return () => { cancelled = true }
  }, [templateId])

  const renderPages = useCallback(async (pdf: any, pageDims: PagePoints[]) => {
    const widths: Record<number, number> = {}
    for (const pd of pageDims) {
      const canvas = canvasRefs.current[pd.page]
      if (!canvas) { addDiag(`p${pd.page}: NO canvas element`); continue }
      const page = await pdf.getPage(pd.page)
      const measured = canvas.parentElement?.clientWidth ?? 0
      const containerW = measured > 40 ? measured : (typeof window !== 'undefined' ? Math.min(window.innerWidth - 32, 800) : 800)
      // Fit-to-width baseline × user zoom. Capped so we never exceed safe canvas sizes.
      const fitScale = Math.min(containerW / pd.w, 2)
      const scale = Math.max(0.2, Math.min(fitScale * zoom, 6))
      const vp = page.getViewport({ scale })
      canvas.width = vp.width
      canvas.height = vp.height
      widths[pd.page] = vp.width
      const cctx = canvas.getContext('2d')
      if (!cctx) { addDiag(`p${pd.page}: NO 2d context (measured ${measured})`); continue }
      try {
        await page.render({ canvasContext: cctx, viewport: vp }).promise
        addDiag(`p${pd.page}: rendered ${Math.round(vp.width)}×${Math.round(vp.height)} (measured ${measured})`)
      } catch (re: any) {
        addDiag(`p${pd.page}: render FAILED — ${re?.message || re}`)
      }
    }
    setRenderWidths(widths)
  }, [addDiag, zoom])

  // Render the PDF pages AFTER their canvas elements have mounted. Keyed on `pages` (which drives
  // the canvas mount) so render only runs once the DOM canvases exist — fixes the mobile race where
  // a setTimeout fired before mount and found "NO canvas element". Re-runs on layout + zoom change.
  useEffect(() => {
    if (pdfDocRef.current && pages.length) {
      const id = requestAnimationFrame(() => { void renderPages(pdfDocRef.current, pages) })
      return () => cancelAnimationFrame(id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pages, isNarrow, zoom])

  function onPagePlace(clientX: number, clientY: number, target: HTMLElement, page: PagePoints) {
    const rect = target.getBoundingClientRect()
    const pixelX = clientX - rect.left
    const pixelY = clientY - rect.top
    // Guard: ignore taps outside the rendered page area.
    if (pixelX < 0 || pixelY < 0 || pixelX > rect.width || pixelY > rect.height) return
    const renderedWidthPx = renderWidths[page.page] ?? rect.width
    const point = pixelToPoint({ page: page.page, pixelX, pixelY, renderedWidthPx }, page)
    const size = DEFAULT_FIELD_SIZE[tool]
    const invalid = validatePlacement(point, page, size.w, size.h)
    if (invalid) { setErr(invalid); return }
    setErr(null); setSaved(false)
    // Text fields can be data-bound (auto-filled). A bound field needs a unique label so the fill
    // engine + DocuSign can target it; derive one from the binding + a counter.
    const isText = tool === 'text'
    const binding = isText ? dataBinding : ''
    const autoLabel = binding
      ? `${binding}_${fields.filter(f => f.data_binding === binding).length + 1}`
      : (isText ? `text_${fields.filter(f => f.field_type === 'text').length + 1}` : undefined)
    setFields(f => [...f, {
      _key: `f${Date.now()}`, field_type: tool, recipient_role: recipient,
      page_number: page.page, pos_x: point.x, pos_y: point.y,
      width: size.w, height: size.h, is_required: true,
      label: autoLabel, data_binding: binding || undefined,
    }])
  }

  // Page-level pointer: track pointers for pinch-zoom; a single tap (no pinch, no drag) places.
  function onPagePointerDown(e: React.PointerEvent, _page: PagePoints) {
    pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY })
    if (pointersRef.current.size === 2) {
      // Begin pinch: record starting finger distance + current zoom.
      const pts = [...pointersRef.current.values()]
      const dist = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y)
      pinchRef.current = { startDist: dist, startZoom: zoom }
    } else if (pointersRef.current.size === 1 && !dragRef.current) {
      // Single pointer on the page (not on a marker) → place a field on pointer-up if it didn't pinch.
      ;(e.currentTarget as HTMLElement).dataset.tapCandidate = '1'
    }
  }
  function onPagePointerMove(e: React.PointerEvent) {
    if (pointersRef.current.has(e.pointerId))
      pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY })
    if (pinchRef.current && pointersRef.current.size === 2) {
      const pts = [...pointersRef.current.values()]
      const dist = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y)
      const next = Math.max(0.5, Math.min(pinchRef.current.startZoom * (dist / pinchRef.current.startDist), 5))
      setZoom(Math.round(next * 100) / 100)
      ;(e.currentTarget as HTMLElement).dataset.tapCandidate = ''   // a pinch is not a tap
    }
  }
  function onPagePointerUp(e: React.PointerEvent, page: PagePoints) {
    const wasPinch = pointersRef.current.size === 2
    pointersRef.current.delete(e.pointerId)
    if (pointersRef.current.size < 2) pinchRef.current = null
    const el = e.currentTarget as HTMLElement
    // Place only on a clean single tap (no pinch, no active drag).
    if (!wasPinch && !dragRef.current && el.dataset.tapCandidate === '1') {
      onPagePlace(e.clientX, e.clientY, el, page)
    }
    el.dataset.tapCandidate = ''
  }

  // ── Drag a placed field to reposition ───────────────────────────────────────────────────────
  function onFieldPointerDown(e: React.PointerEvent, key: string, page: PagePoints) {
    e.stopPropagation()  // don't let the page treat this as a placement tap
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
    dragRef.current = { key, page, offX: e.clientX - rect.left, offY: e.clientY - rect.top }
    ;(e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId)
  }
  function onFieldPointerMove(e: React.PointerEvent, page: PagePoints) {
    const d = dragRef.current
    if (!d || d.key !== dragRef.current?.key) return
    e.stopPropagation()
    const pageWrapEl = (e.currentTarget as HTMLElement).parentElement
    if (!pageWrapEl) return
    const rect = pageWrapEl.getBoundingClientRect()
    const renderedWidthPx = renderWidths[page.page] ?? rect.width
    // Convert the marker's top-left (pointer minus grab offset) to PDF points.
    const pixelX = e.clientX - rect.left - d.offX
    const pixelY = e.clientY - rect.top - d.offY
    const clampedX = Math.max(0, Math.min(pixelX, rect.width))
    const clampedY = Math.max(0, Math.min(pixelY, rect.height))
    const point = pixelToPoint({ page: page.page, pixelX: clampedX, pixelY: clampedY, renderedWidthPx }, page)
    setFields(f => f.map(x => x._key === d.key ? { ...x, pos_x: point.x, pos_y: point.y } : x))
    setSaved(false)
  }
  function onFieldPointerUp(e: React.PointerEvent) {
    if (dragRef.current) { e.stopPropagation(); dragRef.current = null }
  }

  function removeField(key: string) { setFields(f => f.filter(x => x._key !== key)); setSaved(false) }

  async function save() {
    setSaving(true); setErr(null)
    try {
      const payload: api.EsignPlacement[] = fields.map(({ _key, ...p }) => p)
      await api.saveEsignPlacements(templateId, payload)
      setSaved(true); setTimeout(() => setSaved(false), 2500)
    } catch (e: any) { setErr(e?.message || 'Failed to save placements') }
    finally { setSaving(false) }
  }

  // Preview the auto-fill: save current placements first (so the server evaluates what's on screen),
  // then ask the server what each field would fill with. Uses sample data (no real patient needed).
  async function runPreview() {
    setPreviewing(true); setErr(null)
    try {
      const payload: api.EsignPlacement[] = fields.map(({ _key, ...p }) => p)
      await api.saveEsignPlacements(templateId, payload)
      const res = await api.previewEsignFill(templateId)
      setPreview({ data_source: res.data_source, fields: res.fields })
    } catch (e: any) { setErr(e?.message || 'Preview failed') }
    finally { setPreviewing(false) }
  }

  return (
    <div style={{ display: 'flex', flexDirection: isNarrow ? 'column' : 'row', gap: 16, alignItems: isNarrow ? 'stretch' : 'flex-start' }}>
      {/* Palette */}
      <div style={isNarrow ? paletteMobile : palette}>
        <div style={paletteTitle}>Signer</div>
        <select value={recipient} onChange={(e) => setRecipient(e.target.value as RecipientRole)} style={recipientSelect}>
          {RECIPIENT_ROLES.map(r => <option key={r} value={r}>{RECIPIENT_LABEL[r]}</option>)}
        </select>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#64748b', marginBottom: 2 }}>
          <span style={{ width: 12, height: 12, borderRadius: 3, background: RECIPIENT_COLOR[recipient], display: 'inline-block' }} />
          Fields you place now sign as <b>{RECIPIENT_LABEL[recipient]}</b>
        </div>
        <div style={paletteTitle}>Field type</div>
        {FIELD_TYPES.map(ft => {
          const Icon = FIELD_ICON[ft]
          return (
            <button key={ft} onClick={() => setTool(ft)}
                    style={{ ...toolBtn, ...(tool === ft ? { borderColor: FIELD_COLOR[ft], background: '#f0f9ff' } : null) }}>
              <Icon size={15} /> <span>{FIELD_LABEL[ft]}</span>
            </button>
          )
        })}
        {tool === 'text' && (
          <div style={{ marginTop: 2 }}>
            <div style={paletteTitle}>Auto-fill this text field with</div>
            <select value={dataBinding} onChange={(e) => setDataBinding(e.target.value)} style={recipientSelect}>
              {DATA_BINDINGS.map(d => <option key={d.key || 'none'} value={d.key}>{d.label}</option>)}
            </select>
            <div style={{ fontSize: 11, color: '#64748b' }}>
              {dataBinding
                ? 'This box is filled automatically from the patient record and locked.'
                : 'This box is left blank for the signer to complete.'}
            </div>
          </div>
        )}
        <div style={hint}>Choose a signer + field type, then tap the page to drop it. Drag a field to move it; tap × to remove. Pinch or use +/− to zoom.</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
          <button onClick={() => setZoom(z => Math.max(0.5, Math.round((z - 0.25) * 100) / 100))} style={zoomBtn} aria-label="Zoom out">−</button>
          <span style={{ fontSize: 12, color: '#64748b', minWidth: 44, textAlign: 'center' }}>{Math.round(zoom * 100)}%</span>
          <button onClick={() => setZoom(z => Math.min(5, Math.round((z + 0.25) * 100) / 100))} style={zoomBtn} aria-label="Zoom in">+</button>
          <button onClick={() => setZoom(1)} style={{ ...zoomBtn, width: 'auto', padding: '0 10px', fontSize: 12 }} aria-label="Reset zoom">Fit</button>
        </div>
        <button onClick={save} disabled={saving} style={saveBtn}>
          {saving ? 'Saving…' : <><Save size={15} /> Save placements</>}
        </button>
        {saved && <div style={{ color: '#059669', fontSize: 13, fontWeight: 600, textAlign: 'center' }}><Check size={13} /> Saved</div>}
        <button onClick={runPreview} disabled={previewing} style={previewBtn}>
          {previewing ? 'Checking…' : '🔍 Preview auto-fill'}
        </button>
        {preview && (
          <div style={previewBox}>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>Preview ({preview.data_source} data)</div>
            {preview.fields.length === 0 && <div style={{ color: '#94a3b8' }}>No fields placed yet.</div>}
            {preview.fields.map((f, i) => (
              <div key={i} style={{ marginBottom: 3 }}>
                <span style={{ color: '#64748b' }}>p{f.page} · {f.signer} · {f.field_type}:</span>{' '}
                {f.auto_filled
                  ? <b style={{ color: '#0891b2' }}>{f.fills_with || '(empty)'}</b>
                  : <span style={{ color: '#94a3b8' }}>{f.fills_with}</span>}
              </div>
            ))}
          </div>
        )}
        <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 8 }}>{fields.length} field{fields.length === 1 ? '' : 's'} placed</div>
      </div>

      {/* PDF pages with placed fields */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {err && <div style={errBox}>{err}</div>}
        {diag.length > 0 && (
          <details style={diagBox}>
            <summary style={{ fontWeight: 700, cursor: 'pointer', color: '#64748b' }}>
              Debug info{diag.some(d => d.includes('ERROR') || d.includes('FAILED') || d.includes('MISSING') || d.includes('NO ')) ? ' ⚠️' : ''}
            </summary>
            <div style={{ marginTop: 6 }}>
              {diag.map((d, i) => (
                <div key={i} style={{ color: d.includes('ERROR') || d.includes('FAILED') || d.includes('MISSING') || d.includes('NO ') ? '#dc2626' : '#334155' }}>• {d}</div>
              ))}
            </div>
          </details>
        )}
        {loading ? (
          <div style={loadingBox}>Loading PDF…</div>
        ) : (
          pages.map(pg => (
            <div key={pg.page} style={pageWrap}>
              <div style={pageLabel}>Page {pg.page}</div>
              {/* Horizontal scroll container so a zoomed page can pan. */}
              <div style={{ overflowX: 'auto', overflowY: 'hidden', WebkitOverflowScrolling: 'touch' }}>
                <div
                  style={{ position: 'relative', cursor: 'crosshair', touchAction: 'none', width: `${zoom * 100}%` }}
                  onPointerDown={(e) => onPagePointerDown(e, pg)}
                  onPointerMove={onPagePointerMove}
                  onPointerUp={(e) => onPagePointerUp(e, pg)}
                  onPointerCancel={(e) => onPagePointerUp(e, pg)}
                >
                  <canvas ref={el => { canvasRefs.current[pg.page] = el }} style={{ display: 'block', width: '100%', border: '1px solid #e2e8f0' }} />
                  {/* Placed field markers for this page */}
                  {fields.filter(f => f.page_number === pg.page).map(f => {
                    const rw = renderWidths[pg.page] ?? 0
                    const ft = (f.field_type ?? 'text') as FieldType
                    const px = pointToPixel({ page: pg.page, x: f.pos_x, y: f.pos_y }, pg, rw)
                    const w = (f.width ?? DEFAULT_FIELD_SIZE[ft].w) * (rw / pg.w)
                    const h = (f.height ?? DEFAULT_FIELD_SIZE[ft].h) * (rw / pg.w)
                    const rcolor = (RECIPIENT_COLOR as any)[f.recipient_role ?? ''] ?? '#475569'
                    const color = rcolor
                    return (
                      <div key={f._key} style={{
                        position: 'absolute', left: px.left, top: px.top, width: w, height: h,
                        border: `2px solid ${color}`, background: `${color}22`, borderRadius: 3,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10,
                        color, fontWeight: 700, touchAction: 'none', cursor: 'move', userSelect: 'none',
                      }}
                      onPointerDown={(e) => onFieldPointerDown(e, f._key, pg)}
                      onPointerMove={(e) => onFieldPointerMove(e, pg)}
                      onPointerUp={onFieldPointerUp}
                      onPointerCancel={onFieldPointerUp}
                      title={`${(RECIPIENT_LABEL as any)[f.recipient_role ?? ''] ?? f.recipient_role}: ${FIELD_LABEL[ft]}${f.data_binding ? ` (auto: ${f.data_binding})` : ''} — drag to move`}>
                        {f.data_binding
                          ? `⚡ ${f.data_binding}`
                          : `${((RECIPIENT_LABEL as any)[f.recipient_role ?? ''] ?? '').slice(0, 3)} · ${FIELD_LABEL[ft]}`}
                        {/* × remove — separate target so dragging doesn't delete */}
                        <span
                          onPointerDown={(e) => { e.stopPropagation(); removeField(f._key) }}
                          style={{ position: 'absolute', top: -9, right: -9, width: 18, height: 18, borderRadius: 9, background: '#dc2626', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, lineHeight: 1, cursor: 'pointer' }}
                          aria-label="Remove field">×</span>
                      </div>
                    )
                })}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  )
}

const palette: React.CSSProperties = { width: 180, flexShrink: 0, position: 'sticky', top: 16, display: 'flex', flexDirection: 'column', gap: 6, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 14 }
const paletteMobile: React.CSSProperties = { width: '100%', display: 'flex', flexDirection: 'column', gap: 6, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 14 }
const zoomBtn: React.CSSProperties = { width: 32, height: 32, borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer', fontSize: 18, fontWeight: 700, color: '#334155', display: 'flex', alignItems: 'center', justifyContent: 'center' }
const paletteTitle: React.CSSProperties = { fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: '#94a3b8', marginBottom: 4 }
const toolBtn: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 500, color: '#334155', textAlign: 'left' }
const hint: React.CSSProperties = { fontSize: 11.5, color: '#94a3b8', lineHeight: 1.4, margin: '8px 0', padding: '8px', background: '#f8fafc', borderRadius: 6 }
const saveBtn: React.CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '10px', borderRadius: 8, border: 'none', background: '#0891b2', color: '#fff', fontWeight: 600, fontSize: 14, cursor: 'pointer', marginTop: 4 }
const pageWrap: React.CSSProperties = { marginBottom: 20 }
const pageLabel: React.CSSProperties = { fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 6 }
const errBox: React.CSSProperties = { background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', padding: 10, borderRadius: 8, marginBottom: 12, fontSize: 13 }
const diagBox: React.CSSProperties = { background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: 10, marginBottom: 12, fontSize: 12, fontFamily: 'monospace', lineHeight: 1.6 }
const previewBtn: React.CSSProperties = { padding: '8px 12px', borderRadius: 8, border: '1px solid #cbd5e1', background: '#fff', color: '#0e7490', fontWeight: 600, fontSize: 13, cursor: 'pointer', marginTop: 6 }
const previewBox: React.CSSProperties = { background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: 10, marginTop: 6, fontSize: 12, lineHeight: 1.5 }
const recipientSelect: React.CSSProperties = { width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13, color: '#334155', background: '#fff', marginBottom: 4 }
const loadingBox: React.CSSProperties = { padding: 40, textAlign: 'center', color: '#64748b' }
